import dayjs from 'dayjs';

import type {
  AnalyticsFilterOption,
  AnalyticsMeasureSums,
  AnalyticsMetricResult,
  AnalyticsTrendPoint,
  CommercialMetricId,
} from '~/types/leLens';

import type { CommercialViewState } from './analyticsState';

/** The three scope filter dimensions, and the word each uses in the control bar and the caption ("Product: …"). */
export const FILTER_DIMENSION_LABEL: Record<'product' | 'market' | 'saleChannel', string> = {
  product: 'Product',
  market: 'Market',
  saleChannel: 'Channel',
};

/**
 * Every derived Commercial figure is one of the four raw sums, or a ratio between two of them,
 * computed here from server-supplied sums rather than server-pre-divided values. That is what
 * keeps a cumulative or per-bucket ratio correct: cumulative Margin % is the ratio of the
 * cumulative sums, never the sum (or average) of the per-bucket ratios.
 */

const DOWN_IS_FAVOURABLE: ReadonlySet<CommercialMetricId> = new Set([
  'commercial.refund_rate',
  'commercial.refunds_pct_gm',
  'commercial.refunds_pct_gm_overrides',
  'commercial.refunds',
]);

function emptySums(): AnalyticsMeasureSums {
  return {
    gross_sales_aud: 0,
    margin_aud: 0,
    accounting_refund_amount_aud: 0,
    margin_aud_refund: 0,
    order_bk: 0,
    room_nights: 0,
    cruise_nights: 0,
    total_pax: 0,
    add_to_cart_events: 0,
    gm_ar: 0,
    items: 0,
    margin_luxplus_aud: 0,
    packages: 0,
    ttv_ar: 0,
    unique_view_count: 0,
    view_count: 0,
  };
}

function addSums(target: AnalyticsMeasureSums, addend: AnalyticsMeasureSums): void {
  target.gross_sales_aud += addend.gross_sales_aud;
  target.margin_aud += addend.margin_aud;
  target.accounting_refund_amount_aud += addend.accounting_refund_amount_aud;
  target.margin_aud_refund = (target.margin_aud_refund ?? 0) + (addend.margin_aud_refund ?? 0);
  target.order_bk += addend.order_bk;
  target.room_nights = (target.room_nights ?? 0) + (addend.room_nights ?? 0);
  target.cruise_nights = (target.cruise_nights ?? 0) + (addend.cruise_nights ?? 0);
  target.total_pax = (target.total_pax ?? 0) + (addend.total_pax ?? 0);
  target.add_to_cart_events = (target.add_to_cart_events ?? 0) + (addend.add_to_cart_events ?? 0);
  target.gm_ar = (target.gm_ar ?? 0) + (addend.gm_ar ?? 0);
  target.items = (target.items ?? 0) + (addend.items ?? 0);
  target.margin_luxplus_aud = (target.margin_luxplus_aud ?? 0) + (addend.margin_luxplus_aud ?? 0);
  target.packages = (target.packages ?? 0) + (addend.packages ?? 0);
  target.ttv_ar = (target.ttv_ar ?? 0) + (addend.ttv_ar ?? 0);
  target.unique_view_count = (target.unique_view_count ?? 0) + (addend.unique_view_count ?? 0);
  target.view_count = (target.view_count ?? 0) + (addend.view_count ?? 0);
}

/** A metric's own reading for the selected period: what a tile shows and what the table's Total row repeats. */
type MetricReading = Pick<
  AnalyticsMetricResult,
  'status' | 'value' | 'comparisonValue' | 'absoluteChange' | 'percentChange'
>;

export type TotalCell = {
  value: number;
  compareValue: number | null;
  change: number | null;
  changePct: number | null;
};

/**
 * The Total row's figures, read off the snapshot's metric rather than summed from the rows: a metric
 * without additive rows (a distinct count, a ratio) still has a total, and it always equals its tile.
 * Null while the metric is missing or unavailable.
 */
export function totalCell(metric: MetricReading | undefined): TotalCell | null {
  if (!metric || metric.status === 'unavailable' || metric.value == null) {
    return null;
  }
  const compareValue = metric.comparisonValue ?? null;
  return {
    value: metric.value,
    compareValue,
    change: metric.absoluteChange ?? (compareValue == null ? null : metric.value - compareValue),
    changePct: metric.percentChange ?? percentChange(metric.value, compareValue),
  };
}

/** The value of one of the 7 Commercial metrics from a raw sums bundle. Null when it cannot be computed (e.g. divide by zero, or no sums yet). */
export function metricValue(id: CommercialMetricId, sums: AnalyticsMeasureSums | null | undefined): number | null {
  if (!sums) {
    return null;
  }
  switch (id) {
    case 'commercial.ttv_br':
      return sums.gross_sales_aud;
    case 'commercial.gross_margin_br':
      return sums.margin_aud;
    case 'commercial.margin_pct_br':
      return sums.gross_sales_aud === 0 ? null : sums.margin_aud / sums.gross_sales_aud;
    case 'commercial.ttv_ar':
      return sums.gross_sales_aud - sums.accounting_refund_amount_aud;
    case 'commercial.gross_margin_ar':
      return sums.margin_aud - (sums.margin_aud_refund ?? 0);
    case 'commercial.margin_pct_ar': {
      const ttvAr = sums.gross_sales_aud - sums.accounting_refund_amount_aud;
      return ttvAr === 0 ? null : (sums.margin_aud - (sums.margin_aud_refund ?? 0)) / ttvAr;
    }
    case 'commercial.orders':
      return sums.order_bk;
    case 'commercial.aov_br':
      return sums.order_bk === 0 ? null : sums.gross_sales_aud / sums.order_bk;
    case 'commercial.aov_ar':
      return sums.order_bk === 0 ? null : (sums.gross_sales_aud - sums.accounting_refund_amount_aud) / sums.order_bk;
    case 'commercial.refund_rate':
      return sums.gross_sales_aud === 0 ? null : sums.accounting_refund_amount_aud / sums.gross_sales_aud;
    // Refunded margin over gross margin, the Key Tracking Report's Refunds%. Recomputed from the
    // two sums so a rolled-up or cumulative value stays a ratio of totals, never an average of ratios.
    case 'commercial.refunds_pct_gm':
      return !sums.margin_aud || sums.margin_aud_refund === undefined ? null : sums.margin_aud_refund / sums.margin_aud;
    case 'commercial.gross_margin_br_overrides':
      return sums.margin_br_overrides ?? null;
    case 'commercial.gross_margin_ar_overrides':
      return sums.margin_br_overrides === undefined
        ? null
        : sums.margin_br_overrides - (sums.margin_refund_overrides ?? 0);
    case 'commercial.margin_pct_br_overrides':
      return sums.gross_sales_aud === 0 || sums.margin_br_overrides === undefined
        ? null
        : sums.margin_br_overrides / sums.gross_sales_aud;
    case 'commercial.margin_pct_ar_overrides': {
      const ttvArOverrides = sums.gross_sales_aud - sums.accounting_refund_amount_aud;
      return ttvArOverrides === 0 || sums.margin_br_overrides === undefined
        ? null
        : (sums.margin_br_overrides - (sums.margin_refund_overrides ?? 0)) / ttvArOverrides;
    }
    case 'commercial.refunds_pct_gm_overrides':
      return !sums.margin_br_overrides || sums.margin_refund_overrides === undefined
        ? null
        : sums.margin_refund_overrides / sums.margin_br_overrides;
    case 'commercial.refunds':
      return sums.accounting_refund_amount_aud;
    case 'commercial.room_nights':
      return sums.room_nights ?? null;
    case 'commercial.cruise_nights':
      return sums.cruise_nights ?? null;
    case 'commercial.pax':
    case 'commercial.total_pax':
      return sums.total_pax ?? null;
    case 'commercial.ttv_per_room_night_br':
      return !sums.room_nights ? null : sums.gross_sales_aud / sums.room_nights;
    case 'commercial.ttv_per_room_night_ar':
      return !sums.room_nights ? null : (sums.gross_sales_aud - sums.accounting_refund_amount_aud) / sums.room_nights;
    case 'commercial.gm_per_room_night_br':
      return !sums.room_nights ? null : sums.margin_aud / sums.room_nights;
    case 'commercial.gm_per_room_night_ar':
      return !sums.room_nights ? null : (sums.margin_aud - (sums.margin_aud_refund ?? 0)) / sums.room_nights;
    case 'commercial.adr_br':
      return !sums.room_nights ? null : sums.gross_sales_aud / sums.room_nights;
    case 'commercial.adr_ar':
      return !sums.room_nights ? null : (sums.gross_sales_aud - sums.accounting_refund_amount_aud) / sums.room_nights;
    case 'commercial.avg_package_value_br':
      return !sums.packages ? null : sums.gross_sales_aud / sums.packages;
    case 'commercial.avg_package_value_ar':
      return !sums.packages ? null : (sums.gross_sales_aud - sums.accounting_refund_amount_aud) / sums.packages;
    case 'commercial.gm_per_package_br':
      return !sums.packages ? null : sums.margin_aud / sums.packages;
    case 'commercial.gm_per_package_ar':
      return !sums.packages ? null : (sums.margin_aud - (sums.margin_aud_refund ?? 0)) / sums.packages;
    case 'commercial.ttv_per_pax_br':
      return !sums.total_pax ? null : sums.gross_sales_aud / sums.total_pax;
    case 'commercial.ttv_per_pax_ar':
      return !sums.total_pax ? null : (sums.gross_sales_aud - sums.accounting_refund_amount_aud) / sums.total_pax;
    case 'commercial.gm_per_pax_br':
      return !sums.total_pax ? null : sums.margin_aud / sums.total_pax;
    case 'commercial.gm_per_pax_ar':
      return !sums.total_pax ? null : (sums.margin_aud - (sums.margin_aud_refund ?? 0)) / sums.total_pax;
    case 'commercial.ttv_per_cruise_night_br':
      return !sums.cruise_nights ? null : sums.gross_sales_aud / sums.cruise_nights;
    case 'commercial.ttv_per_cruise_night_ar':
      return !sums.cruise_nights
        ? null
        : (sums.gross_sales_aud - sums.accounting_refund_amount_aud) / sums.cruise_nights;
    case 'commercial.total_views':
      return sums.view_count ?? null;
    case 'commercial.unique_views':
      return sums.unique_view_count ?? null;
    case 'commercial.packages':
    case 'commercial.packages_attributed':
      return sums.packages ?? null;
    case 'commercial.items':
      return sums.items ?? null;
    case 'commercial.conversion_rate':
      return !sums.view_count ? null : (sums.packages ?? 0) / sums.view_count;
    case 'commercial.add_to_cart_rate':
      return !sums.view_count ? null : (sums.add_to_cart_events ?? 0) / sums.view_count;
    case 'commercial.revenue_per_view':
    case 'commercial.revenue_per_view_br':
      return !sums.view_count ? null : sums.gross_sales_aud / sums.view_count;
    case 'commercial.revenue_per_view_ar':
      return !sums.view_count ? null : (sums.ttv_ar ?? 0) / sums.view_count;
    case 'commercial.margin_per_view':
    case 'commercial.margin_per_view_br':
      return !sums.view_count ? null : sums.margin_aud / sums.view_count;
    case 'commercial.margin_per_view_ar':
      return !sums.view_count ? null : (sums.gm_ar ?? 0) / sums.view_count;
    case 'commercial.margin_per_view_luxplus':
      return !sums.view_count ? null : (sums.margin_luxplus_aud ?? 0) / sums.view_count;
    case 'commercial.ttv_br_attributed':
      return sums.gross_sales_aud;
    case 'commercial.ttv_ar_attributed':
      return sums.ttv_ar ?? null;
    case 'commercial.gross_margin_br_attributed':
      return sums.margin_aud;
    case 'commercial.gross_margin_ar_attributed':
      return sums.gm_ar ?? null;
    default:
      return null;
  }
}

/** `(current - previous) / |previous|`. Null when either side is missing or previous is zero (no meaningful percent). */
export function percentChange(current: number | null | undefined, previous: number | null | undefined): number | null {
  if (current == null || previous == null || previous === 0) {
    return null;
  }
  return (current - previous) / Math.abs(previous);
}

/**
 * Whether `change` is good news for metric `id`. Favourable is "higher" for TTV, GM, Margin %,
 * Orders and AOV, and "lower" for Refunds and Refund rate (00_DESIGN_DIRECTION.md principle 5).
 * Null when there is nothing to judge (no change, or change is exactly zero).
 */
export function isFavourable(id: CommercialMetricId, change: number | null | undefined): boolean | null {
  if (change == null || change === 0) {
    return null;
  }
  const wantsUp = !DOWN_IS_FAVOURABLE.has(id);
  return wantsUp ? change > 0 : change < 0;
}

/** `part / total`. Null when either is missing or `total` is zero. */
export function share(part: number | null | undefined, total: number | null | undefined): number | null {
  if (part == null || total == null || total === 0) {
    return null;
  }
  return part / total;
}

/** A group's share of the total change in the focus metric vs. the compare period: `groupChange / totalChange`. */
export function contribution(
  groupChange: number | null | undefined,
  totalChange: number | null | undefined,
): number | null {
  if (groupChange == null || totalChange == null || totalChange === 0) {
    return null;
  }
  return groupChange / totalChange;
}

export type CumulativePoint = {
  bucket: string;
  current: number | null;
  comparison: number | null;
};

/**
 * Running total of metric `id` across `points`, in the order given. Recomputes ratio metrics
 * (Margin %, AOV, Refund rate) from the accumulated sums at each step, rather than accumulating
 * the per-bucket ratios themselves. `inPeriodOnly` (default true, matching the trend surface's
 * "Cumulative = running total inside the selected period") drops buckets outside the reporting
 * period before accumulating; pass false to accumulate the full chart window instead.
 */
export function cumulativeSeries(
  points: ReadonlyArray<AnalyticsTrendPoint>,
  id: CommercialMetricId,
  inPeriodOnly = true,
): Array<CumulativePoint> {
  const relevant = inPeriodOnly ? points.filter((point) => point.inPeriod) : points;
  const runningCurrent = emptySums();
  const runningComparison = emptySums();
  let comparisonSeen = false;
  return relevant.map((point) => {
    addSums(runningCurrent, point.current);
    if (point.comparison) {
      addSums(runningComparison, point.comparison);
      comparisonSeen = true;
    }
    return {
      bucket: point.bucket,
      current: metricValue(id, runningCurrent),
      comparison: comparisonSeen ? metricValue(id, runningComparison) : null,
    };
  });
}

export type ChangePoint = {
  bucket: string;
  change: number | null;
};

/** Per-bucket percent change of metric `id`, current vs. comparison, one entry per point in `points`. */
export function changeSeries(points: ReadonlyArray<AnalyticsTrendPoint>, id: CommercialMetricId): Array<ChangePoint> {
  return points.map((point) => ({
    bucket: point.bucket,
    change: percentChange(metricValue(id, point.current), point.comparison ? metricValue(id, point.comparison) : null),
  }));
}

/** The metric strip's 32px sparkline: metric `id`'s current-series value at every point in the chart window, in order. */
export function sparklineSeries(
  points: ReadonlyArray<AnalyticsTrendPoint>,
  id: CommercialMetricId,
): Array<number | null> {
  return points.map((point) => metricValue(id, point.current));
}

// ---------------------------------------------------------------------------
// Formatters
// ---------------------------------------------------------------------------

const COMPACT_CURRENCY_IDS: ReadonlySet<CommercialMetricId> = new Set([
  'commercial.ttv_br',
  'commercial.ttv_ar',
  'commercial.gross_margin_br',
  'commercial.gross_margin_ar',
  'commercial.gross_margin_br_overrides',
  'commercial.gross_margin_ar_overrides',
  'commercial.refunds',
  'commercial.ttv_br_attributed',
  'commercial.ttv_ar_attributed',
  'commercial.gross_margin_br_attributed',
  'commercial.gross_margin_ar_attributed',
]);
const FULL_CURRENCY_IDS: ReadonlySet<CommercialMetricId> = new Set([
  'commercial.aov_br',
  'commercial.aov_ar',
  'commercial.adr_br',
  'commercial.adr_ar',
  'commercial.avg_package_value_br',
  'commercial.avg_package_value_ar',
  'commercial.gm_per_package_br',
  'commercial.gm_per_package_ar',
  'commercial.ttv_per_pax_br',
  'commercial.ttv_per_pax_ar',
  'commercial.gm_per_pax_br',
  'commercial.gm_per_pax_ar',
  'commercial.ttv_per_cruise_night_br',
  'commercial.ttv_per_cruise_night_ar',
  'commercial.ttv_per_room_night_br',
  'commercial.ttv_per_room_night_ar',
  'commercial.gm_per_room_night_br',
  'commercial.gm_per_room_night_ar',
  'commercial.revenue_per_view',
  'commercial.revenue_per_view_br',
  'commercial.revenue_per_view_ar',
  'commercial.margin_per_view',
  'commercial.margin_per_view_br',
  'commercial.margin_per_view_ar',
  'commercial.margin_per_view_luxplus',
]);
const PERCENT_IDS: ReadonlySet<CommercialMetricId> = new Set([
  'commercial.margin_pct_br',
  'commercial.margin_pct_ar',
  'commercial.margin_pct_br_overrides',
  'commercial.margin_pct_ar_overrides',
  'commercial.refund_rate',
  'commercial.refunds_pct_gm',
  'commercial.refunds_pct_gm_overrides',
  'commercial.conversion_rate',
  'commercial.add_to_cart_rate',
]);
const COUNT_IDS: ReadonlySet<CommercialMetricId> = new Set([
  'commercial.orders',
  'commercial.room_nights',
  'commercial.cruise_nights',
  'commercial.pax',
  'commercial.total_pax',
  'commercial.total_views',
  'commercial.unique_views',
  'commercial.packages',
  'commercial.packages_attributed',
  'commercial.items',
]);

/** From here money reads compact ($86K, $964K, $1.20M), so a column of amounts never mixes "$86,292" with "$964K". */
const COMPACT_CURRENCY_FLOOR_AUD = 10_000;

/** `$42.23M` style: compact notation, 2 dp above $1M, 0 dp below. Used for TTV, GM and Refunds tiles. */
function formatCompactCurrencyAud(value: number): string {
  const compact = Math.abs(value) >= COMPACT_CURRENCY_FLOOR_AUD;
  return new Intl.NumberFormat('en-AU', {
    currency: 'AUD',
    currencyDisplay: 'narrowSymbol',
    maximumFractionDigits: Math.abs(value) >= 1_000_000 ? 2 : 0,
    minimumFractionDigits: Math.abs(value) >= 1_000_000 ? 2 : 0,
    notation: compact ? 'compact' : 'standard',
    style: 'currency',
  }).format(value);
}

/** `$1,905.28` style: full precision, always 2 dp, never compact. Used for AOV. */
function formatFullCurrencyAud(value: number): string {
  return new Intl.NumberFormat('en-AU', {
    currency: 'AUD',
    currencyDisplay: 'narrowSymbol',
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
    style: 'currency',
  }).format(value);
}

/**
 * The compact percentage language used throughout Commercial. Precision is chosen from the
 * underlying percentage-point value (not from an already-rounded display value), so small but
 * meaningful rates such as 0.305% remain 0.31% rather than becoming a padded 0.3%.
 */
export function formatPercentage(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) {
    return 'Unavailable';
  }
  const percentagePoints = value * 100;
  const absolute = Math.abs(percentagePoints);
  const fractionDigits = absolute < 10 ? 2 : absolute < 100 ? 1 : 0;
  // Correct binary representation noise at a display midpoint (0.00305 * 100 can become
  // 0.30499999999999999) without changing the underlying value or its precision rule.
  const roundingSafe = percentagePoints + Math.sign(percentagePoints || 1) * Number.EPSILON * Math.max(1, absolute);
  return `${roundingSafe.toFixed(fractionDigits)}%`;
}

/** A change as one decimal place at every size, so a column of changes lines up: "0.6%", "18.5%", "112.0%". */
export function formatChangePercentage(value: number): string {
  const percentagePoints = value * 100;
  return `${(percentagePoints + Number.EPSILON * Math.max(1, percentagePoints)).toFixed(1)}%`;
}

/** Renders one of the 7 metrics' values per 00_DESIGN_DIRECTION.md's typography rules. `'Unavailable'` for a missing value. */
export function formatMetric(id: CommercialMetricId, value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) {
    return 'Unavailable';
  }
  if (COMPACT_CURRENCY_IDS.has(id)) {
    return formatCompactCurrencyAud(value);
  }
  if (FULL_CURRENCY_IDS.has(id)) {
    return formatFullCurrencyAud(value);
  }
  if (PERCENT_IDS.has(id)) {
    return formatPercentage(value);
  }
  if (COUNT_IDS.has(id)) {
    return new Intl.NumberFormat('en-AU', { maximumFractionDigits: 0 }).format(value);
  }
  return new Intl.NumberFormat('en-AU', { maximumFractionDigits: 0 }).format(value);
}

/** `"↑ 11.0%"` / `"↓ 12.6%"`. Null when there is nothing to show (caller renders its own "Comparison unavailable" copy). */
export function formatDelta(change: number | null | undefined): string | null {
  if (change == null || !Number.isFinite(change)) {
    return null;
  }
  const arrow = change >= 0 ? '↑' : '↓';
  return `${arrow} ${formatChangePercentage(Math.abs(change))}`;
}

/**
 * A tile's change line. Rates move in points ("↓ 0.1 pt"), because "margin % down 0.4%" would be a percent of
 * a percent; every other metric moves in percent. Null when there is no comparison to show.
 */
export function formatMetricDelta(
  id: CommercialMetricId,
  metric: { value?: number | null; comparisonValue?: number | null; percentChange?: number | null },
): string | null {
  if (!PERCENT_IDS.has(id)) {
    return formatDelta(metric.percentChange);
  }
  const { value, comparisonValue } = metric;
  if (value == null || comparisonValue == null || !Number.isFinite(value) || !Number.isFinite(comparisonValue)) {
    return null;
  }
  const points = (value - comparisonValue) * 100;
  return `${points >= 0 ? '↑' : '↓'} ${Math.abs(points).toFixed(1)} pt`;
}

/** `"1–10 Sep 2026"`: en dash, drops the repeated month (and year, if also shared) from the start date. */
export function formatRangeLabel(from?: string | null, to?: string | null): string | null {
  if (!from || !to) {
    return null;
  }
  const start = dayjs(from);
  const end = dayjs(to);
  if (!start.isValid() || !end.isValid()) {
    return null;
  }
  const sameYear = start.year() === end.year();
  const sameMonth = sameYear && start.month() === end.month();
  if (start.isSame(end, 'day')) {
    return end.format('D MMM YYYY');
  }
  const startFormat = sameMonth ? 'D' : sameYear ? 'D MMM' : 'D MMM YYYY';
  return `${start.format(startFormat)}–${end.format('D MMM YYYY')}`;
}

function filterClause(
  dimension: 'product' | 'market' | 'saleChannel',
  ids: ReadonlyArray<string>,
  options: ReadonlyArray<AnalyticsFilterOption>,
): string | null {
  if (!ids.length) {
    return null;
  }
  const labels = ids.map((id) => options.find((option) => option.id === id)?.label ?? id);
  return `${FILTER_DIMENSION_LABEL[dimension]}: ${labels.join(', ')}`;
}

type CaptionPeriod = { from: string; to: string; comparison: { from: string; to: string } | null };
type FilterOptionLists = {
  product: ReadonlyArray<AnalyticsFilterOption>;
  market: ReadonlyArray<AnalyticsFilterOption>;
  saleChannel: ReadonlyArray<AnalyticsFilterOption>;
};

/**
 * `"1–10 Sep 2026 vs 1–10 Sep 2025"`. `state.comparison` decides the wording when the snapshot has not
 * resolved a comparison range: an explicit "no comparison" only appears when the reader chose `none`.
 */
export function formatPeriodDates(
  snapshotPeriod: CaptionPeriod,
  state: Pick<CommercialViewState, 'comparison'>,
): string {
  const rangeLabel = formatRangeLabel(snapshotPeriod.from, snapshotPeriod.to) ?? '';
  const comparisonLabel = snapshotPeriod.comparison
    ? formatRangeLabel(snapshotPeriod.comparison.from, snapshotPeriod.comparison.to)
    : null;
  if (comparisonLabel) {
    return `${rangeLabel} vs ${comparisonLabel}`;
  }
  return state.comparison === 'none' ? `${rangeLabel} · no comparison` : rangeLabel;
}

/** The Explore header's context: `"1 to 16 Oct 2026 vs 1 to 16 Oct 2025 · AUD · Sydney time"`. */
export function formatExploreContext(
  snapshotPeriod: CaptionPeriod,
  state: Pick<CommercialViewState, 'comparison'>,
): string {
  return [formatPeriodDates(snapshotPeriod, state).replace(/–/g, ' to '), 'AUD', 'Sydney time'].join(' · ');
}

/** One "Dimension: value[, value…]" clause per active scope filter, resolved to option labels; none for "All". */
function scopeClauses(
  state: Pick<CommercialViewState, 'filters'> & { marginMaxedOnly?: boolean },
  filterOptions: FilterOptionLists,
): string[] {
  return [
    filterClause('product', state.filters.product, filterOptions.product),
    filterClause('market', state.filters.market, filterOptions.market),
    filterClause('saleChannel', state.filters.saleChannel, filterOptions.saleChannel),
    state.marginMaxedOnly ? 'Margin Maxed only' : null,
  ].filter((clause): clause is string => Boolean(clause));
}

/** `"Market: AU · Channel: Web"`, or null when no scope filter is active. */
export function formatFilteredLine(
  state: Pick<CommercialViewState, 'filters'> & { marginMaxedOnly?: boolean },
  filterOptions: FilterOptionLists,
): string | null {
  const clauses = scopeClauses(state, filterOptions);
  return clauses.length ? clauses.join(' · ') : null;
}

/**
 * `"1–10 Sep 2026 vs 1–10 Sep 2025 · Product: Accom (incl. NHA) · AUD · Sydney time · data to Thu
 * 10 Sep"`. `dataTo` reads `snapshotPeriod.to` (equal to the server's `refresh.dataTo`, per
 * 01_IMPLEMENTATION_PLAN.md section 2.1). Scope clauses come right after the dates and before "AUD";
 * `filterOptions` is typically the same option lists passed to `ControlBar`.
 */
export function formatCaption(
  snapshotPeriod: CaptionPeriod,
  state: Pick<CommercialViewState, 'comparison' | 'filters'> & { marginMaxedOnly?: boolean },
  filterOptions: FilterOptionLists,
): string {
  const dataTo = `data to ${dayjs(snapshotPeriod.to).format('ddd D MMM')}`;
  return [formatPeriodDates(snapshotPeriod, state), ...scopeClauses(state, filterOptions), 'AUD', 'Sydney time', dataTo]
    .filter(Boolean)
    .join(' · ');
}
